import { useEffect, useState } from "react"
import { Alert, Form, Input } from "antd"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { AxiosError } from "axios"
import { useAuth } from "../../../components/contexts/AuthContext"
import { getLoginColorAndLogo, getLoginLogo } from "../api/apiCalls"
import { hexToHsl } from "../../../services/functions"
import { resetSessionExpired } from "../../../auth/AuthenticatedAxios"

type TLoginForm = {
    email: string
    password: string
}

const loginErrorText = (error: unknown) => {
    const response = (error as AxiosError<{ displayMessage?: string }>)?.response
    if (response?.data?.displayMessage) return response.data.displayMessage
    if (response?.status === 400 || response?.status === 401) return "E-Mail oder Passwort falsch"
    return "Anmeldung derzeit nicht möglich. Bitte versuchen Sie es später erneut."
}

export default function Login() {
    const auth = useAuth()
    const [submitting, setSubmitting] = useState(false)
    const [errorText, setErrorText] = useState<string | undefined>(undefined)

    // nach einem Sitzungsablauf unterdrückt messageError alle Toasts; auf der Login-Seite beginnt ein neuer Anlauf
    useEffect(() => {
        resetSessionExpired()
    }, [])

    const { data: colorAndLogo } = useQuery({
        queryKey: ["login_color_and_logo"],
        queryFn: getLoginColorAndLogo,
        retry: false,
    })
    const { data: logo } = useQuery({
        queryKey: ["login_logo"],
        queryFn: getLoginLogo,
        enabled: !!colorAndLogo?.logo?.s3Key,
        retry: false,
    })

    useEffect(() => {
        if (colorAndLogo?.color) {
            document.documentElement.style.setProperty("--p", hexToHsl(`${colorAndLogo.color}`))
            document.documentElement.style.setProperty("--p-hex", `${colorAndLogo.color}`)
            document.documentElement.style.setProperty("--p-hex-50", `${colorAndLogo.color}50`)
        }
    }, [colorAndLogo])

    const onFinish = async (values: TLoginForm) => {
        setSubmitting(true)
        setErrorText(undefined)
        try {
            // nach Erfolg leitet App.tsx über ?redirect= auf die angefragte Seite weiter
            await auth.login(values.email.trim(), values.password)
        } catch (error) {
            setErrorText(loginErrorText(error))
            setSubmitting(false)
        }
    }

    return (
        <main className="flex items-center justify-center w-screen min-h-screen bg-base-100 p-4">
            <div className="w-full max-w-md bg-white rounded-default shadow-lg p-8 flex flex-col gap-6">
                <div className="flex justify-center">
                    <img src={logo ?? "/hausmanager-logo.png"} alt="Logo" className="max-h-20 object-contain" />
                </div>
                <h1 className="text-2xl font-semibold text-center">Anmelden</h1>
                {errorText && <Alert type="error" showIcon message={errorText} />}
                <Form<TLoginForm> layout="vertical" requiredMark={false} onFinish={onFinish} disabled={submitting}>
                    <Form.Item
                        label="E-Mail"
                        name="email"
                        rules={[
                            { required: true, message: "Bitte E-Mail-Adresse eingeben" },
                            { type: "email", message: "Bitte eine gültige E-Mail-Adresse eingeben" },
                        ]}>
                        <Input autoComplete="username" autoFocus size="large" />
                    </Form.Item>
                    <Form.Item label="Passwort" name="password" rules={[{ required: true, message: "Bitte Passwort eingeben" }]}>
                        <Input.Password autoComplete="current-password" size="large" />
                    </Form.Item>
                    <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
                        {submitting && <span className="loading loading-spinner" />}
                        Anmelden
                    </button>
                </Form>
                <div className="text-center">
                    <Link to="/passwort/vergessen" className="text-primary hover:underline">
                        Passwort vergessen?
                    </Link>
                </div>
            </div>
        </main>
    )
}
